import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@core/services/snackbar.service';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BriefService } from '@pages/campaigns/brief/brief.service';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { buildBriefStops, TBriefStop } from '../../brief/brief-error-grouping.util';
import { findDeliverableIndexInFlatArray } from '../../brief/brief-mapping.util';
import { IBriefValidationMessage } from '../../brief/brief.interface';
import { BriefWarningsDialogComponent } from './brief-warnings-dialog/brief-warnings-dialog.component';
import { IBriefWizardDialogData, TBriefWizardDialogResult } from './brief-wizard-dialog.interface';
import { parseDate } from './brief-wizard-dialog.util';
import {
  BriefBasicsDatesStepComponent,
  TBriefBasicsDatesValue,
} from './steps/basics-dates-step/basics-dates-step.component';
import {
  BriefBasicsFieldStepComponent,
  IBriefBasicsFieldValue,
} from './steps/basics-field-step/basics-field-step.component';
import { IBriefStepState } from './steps/brief-step.interface';
import { BriefDeliverableItemStepComponent } from './steps/deliverable-item-step/deliverable-item-step.component';
import { BriefExecutionItemStepComponent } from './steps/execution-item-step/execution-item-step.component';
import { BriefPlatformStepComponent, IBriefPlatformValue } from './steps/platform-step/platform-step.component';

@Component({
  imports: [
    SideDialogComponent,
    TranslateModule,
    MatButtonModule,
    DotsSpinnerComponent,
    BriefBasicsDatesStepComponent,
    BriefBasicsFieldStepComponent,
    BriefPlatformStepComponent,
    BriefDeliverableItemStepComponent,
    BriefExecutionItemStepComponent,
  ],
  selector: 'app-brief-wizard-dialog',
  templateUrl: './brief-wizard-dialog.component.html',
})
export class BriefWizardDialogComponent {
  private readonly data = inject<IBriefWizardDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<BriefWizardDialogComponent, TBriefWizardDialogResult>);
  private readonly briefService = inject(BriefService);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly snackbarService = inject(SnackbarService);

  public readonly campaign = signal<Record<string, unknown>>(this.data.analysis.campaign);
  public readonly errors = signal<IBriefValidationMessage[]>(this.data.analysis.errors);
  public readonly warnings = signal<IBriefValidationMessage[]>(this.data.analysis.warnings);
  public readonly validating = signal<boolean>(false);

  public readonly stops = computed<TBriefStop[]>(() => buildBriefStops(this.errors()));
  public readonly currentStopIndex = signal<number>(0);
  public readonly currentStop = computed<TBriefStop | null>(() => this.stops()[this.currentStopIndex()] ?? null);
  public readonly currentStepValid = signal<boolean>(false);

  // Always resolves to a display name for stops scoped to a CTA template (platform/deliverable/
  // execution) - falls back to "Deliverable group N" when the template itself has no name yet.
  // Basics stops have no CTA template in scope, so this stays null for them.
  public readonly currentCtaTemplateName = computed<string | null>(() => {
    const stop = this.currentStop();

    if (
      !stop ||
      stop.kind === 'basicsDates' ||
      stop.kind === 'basicsName' ||
      stop.kind === 'basicsNotes' ||
      stop.kind === 'basicsBudget'
    ) {
      return null;
    }

    const ctaTemplate = this.currentCtaTemplate(stop.templateIndex);
    const name = ctaTemplate?.['name'] as string | null;

    if (name) {
      return name;
    }

    return this.translate.instant('dialogs.createCampaignDialog.briefWizard.deliverableGroupFallback', {
      index: stop.templateIndex + 1,
    });
  });

  // The campaign-basics counterpart to currentCtaTemplateName - resolves to a display name for
  // the specific basics field the current stop fixes, null for every other stop kind.
  public readonly currentBasicsFieldName = computed<string | null>(() => {
    const stop = this.currentStop();

    switch (stop?.kind) {
      case 'basicsName':
        return this.translate.instant('dialogs.createCampaignDialog.briefWizard.fields.name');
      case 'basicsDates':
        return this.translate.instant('dialogs.createCampaignDialog.briefWizard.fields.duration');
      case 'basicsBudget':
        return this.translate.instant('dialogs.createCampaignDialog.briefWizard.fields.budget');
      case 'basicsNotes':
        return this.translate.instant('dialogs.createCampaignDialog.briefWizard.fields.notes');
      default:
        return null;
    }
  });

  // startDate/startTime (and endDate/endTime) are set to the SAME parsed instant - the shared
  // date-time-range-fields component reads the day from one and the time-of-day from the other
  // (see DateTimeRangeFieldsComponent), same convention CampaignBasicsComponent.fillForm() uses.
  public readonly currentBasicsDatesValue = computed<TBriefBasicsDatesValue>(() => {
    const start = parseDate(this.campaign()['start']);
    const end = parseDate(this.campaign()['end']);

    return { endDate: end, endTime: end, startDate: start, startTime: start };
  });

  public readonly currentBasicsFieldValue = computed<IBriefBasicsFieldValue>(() => ({
    budget: (this.campaign()['budget'] as number | null) ?? null,
    currency: (this.campaign()['currency'] as IBriefBasicsFieldValue['currency']) ?? null,
    name: (this.campaign()['name'] as string | null) ?? null,
    notes: (this.campaign()['notes'] as string | null) ?? null,
  }));

  public readonly currentPlatformValue = computed<IBriefPlatformValue>(() => {
    const stop = this.currentStop();

    if (!stop || stop.kind !== 'platform') {
      return { platformFeature: null };
    }

    const ctaTemplate = this.currentCtaTemplate(stop.templateIndex);

    return { platformFeature: (ctaTemplate?.['platformFeature'] as EPlatformFeature | null) ?? null };
  });

  public readonly currentPlatformExistingDeliverableTypes = computed<EDeliverableType[]>(() => {
    const stop = this.currentStop();

    if (!stop || stop.kind !== 'platform') {
      return [];
    }

    const deliverables =
      (this.currentCtaTemplate(stop.templateIndex)?.['deliverables'] as Record<string, unknown>[]) ?? [];

    return deliverables.map((deliverable) => deliverable['type'] as EDeliverableType);
  });

  public readonly currentDeliverableType = computed<EDeliverableType | null>(() => {
    const stop = this.currentStop();

    return stop?.kind === 'deliverable'
      ? ((this.currentCtaTemplate(stop.templateIndex)?.['deliverables'] as Record<string, unknown>[])?.[
          findDeliverableIndexInFlatArray(
            (this.currentCtaTemplate(stop.templateIndex)?.['deliverables'] as Record<string, unknown>[]) ?? [],
            stop.arrayKey,
            stop.itemIndex,
          )
        ]?.['type'] as EDeliverableType | null)
      : null;
  });

  public readonly currentDeliverableValue = computed<Record<string, unknown>>(() => {
    const stop = this.currentStop();

    if (!stop || stop.kind !== 'deliverable') {
      return {};
    }

    const deliverables =
      (this.currentCtaTemplate(stop.templateIndex)?.['deliverables'] as Record<string, unknown>[]) ?? [];
    const index = findDeliverableIndexInFlatArray(deliverables, stop.arrayKey, stop.itemIndex);

    return index === -1 ? {} : deliverables[index];
  });

  public readonly currentExecutionValue = computed<Record<string, unknown>>(() => {
    const stop = this.currentStop();

    if (!stop || stop.kind !== 'execution' || stop.itemIndex === null) {
      return {};
    }

    const executions = (this.currentCtaTemplate(stop.templateIndex)?.['executions'] as Record<string, unknown>[]) ?? [];

    return executions[stop.itemIndex] ?? {};
  });

  // Feeds app-execution the same platformFeature/game-play-duration context the real
  // campaign-form gives it, for whichever CTA template the current stop belongs to - not just
  // 'platform' stops (currentPlatformValue only carries that for its own step).
  public readonly currentTemplatePlatformFeature = computed<EPlatformFeature | null>(() => {
    const stop = this.currentStop();

    if (
      !stop ||
      stop.kind === 'basicsDates' ||
      stop.kind === 'basicsName' ||
      stop.kind === 'basicsNotes' ||
      stop.kind === 'basicsBudget'
    ) {
      return null;
    }

    return (this.currentCtaTemplate(stop.templateIndex)?.['platformFeature'] as EPlatformFeature | null) ?? null;
  });

  public readonly currentGamePlayDurations = computed<{ duration: number | null }[]>(() => {
    const stop = this.currentStop();

    if (!stop || stop.kind !== 'execution') {
      return [];
    }

    const deliverables =
      (this.currentCtaTemplate(stop.templateIndex)?.['deliverables'] as Record<string, unknown>[]) ?? [];

    return deliverables
      .filter((deliverable) => deliverable['type'] === EDeliverableType.gamePlay)
      .map((deliverable) => ({ duration: (deliverable['duration'] as number | null) ?? null }));
  });

  public readonly campaignStartDate = computed<Date | null>(() => parseDate(this.campaign()['start']));
  public readonly campaignEndDate = computed<Date | null>(() => parseDate(this.campaign()['end']));

  // The last stop's Next button is the one that actually sends /brief/validate - labelled "Fix"
  // rather than "Next" so it reads as the action it performs.
  public readonly isLastStop = computed<boolean>(() => this.currentStopIndex() === this.stops().length - 1);

  // Bumped every time campaign() is replaced with a freshly-committed draft (applyCurrentStepValue
  // / a /brief/validate response) - folded into currentStopKey below so a leaf deliverable/
  // execution component is always torn down and rebuilt from the latest data when its stop is
  // revisited, even if it's the very same item as before. Several real leaf components (e.g.
  // GamePlayComponent's gameImageUrl signal) keep plain, component-instance-scoped state that's
  // only ever initialized on construction and never resynced when their [form] input is later
  // rebound to a new FormGroup - if Angular's own @switch/@case reuses that same instance because
  // the stop's identity (kind/templateIndex/arrayKey/itemIndex) didn't change, that stale state
  // (e.g. the game's box art preview) silently survives past the point it should reset.
  private readonly campaignVersion = signal(0);

  // Identifies the specific item a stop edits, not just its kind - used to key the
  // deliverable/execution step components in the template so Angular fully recreates them (and
  // everything nested inside, like an app-file-uploader) when moving between two stops of the
  // same kind, rather than reusing the instance and leaking its internal state across items.
  public readonly currentStopKey = computed<string>(() => {
    const stop = this.currentStop();

    if (!stop) {
      return '';
    }

    const version = this.campaignVersion();

    switch (stop.kind) {
      case 'deliverable':
        return `deliverable:${stop.templateIndex}:${stop.arrayKey}:${stop.itemIndex}:${version}`;
      case 'execution':
        return `execution:${stop.templateIndex}:${stop.itemIndex}:${version}`;
      default:
        return stop.kind;
    }
  });

  private currentStepValue: unknown = null;

  constructor() {
    effect(() => {
      this.currentStop();
      this.currentStepValid.set(false);
      this.currentStepValue = null;
    });

    effect(() => {
      if (this.validating() || this.stops().length > 0) {
        return;
      }

      if (this.errors().length > 0) {
        this.handleUnrecognizedErrors();

        return;
      }

      this.finish();
    });
  }

  public onStepStateChange(state: IBriefStepState<unknown>): void {
    this.currentStepValid.set(state.valid);
    this.currentStepValue = state.value;
  }

  // Navigating between stops (forward or back) never hits the backend - it only patches the
  // local draft, so the step count stays stable while the user works through the current round
  // of issues. /brief/validate only runs once, when Next is pressed on the last stop, to check
  // whether the fixes actually resolved everything (or surfaced a new round of issues).
  public onNextClick(): void {
    if (!this.currentStepValid() || this.validating()) {
      return;
    }

    this.applyCurrentStepValue();

    if (this.currentStopIndex() < this.stops().length - 1) {
      this.currentStopIndex.update((index) => index + 1);

      return;
    }

    this.validating.set(true);
    this.briefService.validateBrief(this.campaign(), true).subscribe({
      // An HTTP-level failure (network/5xx/timeout) - not a campaign-data validation result, so
      // there's no errors[]/warnings[] to act on. The wizard stays open with its current stop
      // untouched so the user's edits aren't lost and they can just retry.
      error: () => {
        this.validating.set(false);
        this.snackbarService.openError(
          this.translate.instant('dialogs.createCampaignDialog.briefWizard.briefValidateError'),
        );
      },
      next: (analysis) => {
        this.campaign.set(analysis.campaign);
        this.campaignVersion.update((version) => version + 1);
        this.errors.set(analysis.errors);
        this.warnings.set(analysis.warnings);
        this.currentStopIndex.set(0);
        this.validating.set(false);
      },
    });
  }

  public onBackClick(): void {
    if (this.currentStopIndex() === 0 || this.validating()) {
      return;
    }

    this.applyCurrentStepValue();
    this.currentStopIndex.update((index) => index - 1);
  }

  // The dialog is disableClose (X icon / backdrop / ESC don't dismiss it) and Cancel routes here
  // too - fixes are lost if the user leaves mid-wizard, so both ask for confirmation first rather
  // than discarding progress on a single click.
  public onCloseClick(): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          cancelText: this.translate.instant('dialogs.createCampaignDialog.briefWizard.leaveConfirm.keepFixing'),
          confirmText: this.translate.instant('dialogs.createCampaignDialog.briefWizard.leaveConfirm.leave'),
          message: this.translate.instant('dialogs.createCampaignDialog.briefWizard.leaveConfirm.message'),
          swapButtons: true,
          title: this.translate.instant('dialogs.createCampaignDialog.briefWizard.leaveConfirm.title'),
        },
        width: '400px',
      })
      .afterClosed()
      .subscribe((leave: boolean) => {
        if (leave) {
          this.dialogRef.close({ outcome: 'backToUpload' });
        }
      });
  }

  // Defensive fallback for buildBriefStops() silently dropping an error path it doesn't recognize
  // (see that function's own doc comment) - errors()/stops() are expected to always agree under
  // the current backend contract (docs/campaign-creation-from-brief.md), so this should never
  // fire, but if it ever does the alternative is treating an unmappable error as "nothing left to
  // fix" and proceeding to create a campaign the backend itself flagged as invalid (Copilot review
  // finding). Surfaces the actual backend message(s) instead of leaving the wizard on a blank,
  // nothing-to-fix-looking screen with a dead Next button, and sends the user back to the upload
  // step rather than attempting to create the campaign.
  private handleUnrecognizedErrors(): void {
    const backendMessages = this.errors()
      .map((error) => error.message)
      .filter((message): message is string => !!message)
      .join(' ');
    const notice = this.translate.instant('dialogs.createCampaignDialog.briefWizard.unrecognizedErrors') as string;

    this.snackbarService.openError(backendMessages ? `${notice} ${backendMessages}` : notice);
    this.dialogRef.close({ outcome: 'backToUpload' });
  }

  private finish(): void {
    const warnings = this.warnings();

    if (!warnings.length) {
      this.dialogRef.close({ campaign: this.campaign(), outcome: 'create' });

      return;
    }

    this.dialog
      .open(BriefWarningsDialogComponent, {
        data: { campaign: this.campaign(), warnings },
        disableClose: true,
        width: '480px',
      })
      .afterClosed()
      .subscribe((continueCreating: boolean) => {
        this.dialogRef.close(
          continueCreating ? { campaign: this.campaign(), outcome: 'create' } : { outcome: 'backToUpload' },
        );
      });
  }

  private applyCurrentStepValue(): void {
    const stop = this.currentStop();

    if (!stop || this.currentStepValue === null) {
      return;
    }

    this.campaign.set(this.applyStopValue(this.campaign(), stop, this.currentStepValue));
    this.campaignVersion.update((version) => version + 1);
  }

  private currentCtaTemplate(templateIndex: number): Record<string, unknown> | undefined {
    return ((this.campaign()['ctaTemplates'] as Record<string, unknown>[]) ?? [])[templateIndex];
  }

  private applyStopValue(campaign: Record<string, unknown>, stop: TBriefStop, value: unknown): Record<string, unknown> {
    const draft = structuredClone(campaign);

    switch (stop.kind) {
      case 'basicsDates': {
        const dates = value as TBriefBasicsDatesValue;

        draft['start'] = dates.startDate && this.dateUtils.combineDateTime(dates.startDate, dates.startTime);
        draft['end'] = dates.endDate && this.dateUtils.combineDateTime(dates.endDate, dates.endTime);

        break;
      }

      case 'basicsName': {
        draft['name'] = (value as IBriefBasicsFieldValue).name;

        break;
      }

      case 'basicsNotes': {
        draft['notes'] = (value as IBriefBasicsFieldValue).notes;

        break;
      }

      case 'basicsBudget': {
        const fields = value as IBriefBasicsFieldValue;

        draft['budget'] = fields.budget;
        draft['currency'] = fields.currency;

        break;
      }

      case 'platform': {
        const ctaTemplates = draft['ctaTemplates'] as Record<string, unknown>[];

        ctaTemplates[stop.templateIndex]['platformFeature'] = (value as IBriefPlatformValue).platformFeature;

        break;
      }

      case 'deliverable': {
        const ctaTemplates = draft['ctaTemplates'] as Record<string, unknown>[];
        const deliverables = (ctaTemplates[stop.templateIndex]['deliverables'] as Record<string, unknown>[]) ?? [];
        const index = findDeliverableIndexInFlatArray(deliverables, stop.arrayKey, stop.itemIndex);

        if (index !== -1) {
          deliverables[index] = value as Record<string, unknown>;
        }

        break;
      }

      case 'execution': {
        const ctaTemplates = draft['ctaTemplates'] as Record<string, unknown>[];
        const executions = (
          (ctaTemplates[stop.templateIndex]['executions'] as Record<string, unknown>[]) ?? []
        ).slice();

        if (stop.itemIndex === null) {
          executions.push(value as Record<string, unknown>);
        } else {
          executions[stop.itemIndex] = value as Record<string, unknown>;
        }

        ctaTemplates[stop.templateIndex]['executions'] = executions;

        break;
      }
    }

    return draft;
  }
}
